import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Truck,
  ArrowRight,
  MoreHorizontal,
  Network,
  CheckCircle2,
  ShieldCheck,
  Warehouse,
  CalendarDays,
} from "lucide-react";
import { Crud_Catalogos } from "../../../../herramientas/Crud_Catalogos";
import ListaTransportistas from "./ListaTransportistas";

const PALETTE = {
  primary: "#09478C",
  primaryDark: "#003166",
  primarySoft: "rgba(9, 71, 140, 0.08)",
  primaryBorder: "rgba(9, 71, 140, 0.20)",
  emeraldBg: "#D1FAE5",
  emeraldText: "#065F46",
  greenAccent: "#059669",
  amber: "#B45309",
  amberBg: "#FEF3C7",
  text: "#0F1C2C",
  textVariant: "#424751",
  textMuted: "#737782",
  outlineVariant: "#C3C6D2",
  border: "#E5E7EB",
  borderSoft: "#EEF4FF",
  surface: "#FFFFFF",
  surfaceBright: "#F8F9FF",
  surfaceAlt: "#F8F9FF",
  surfaceContainer: "#E5EFFF",
  surfaceContainerLow: "#EEF4FF",
  surfaceContainerHigh: "#DBE9FE",
  surfaceHighest: "#D6E4F9",
};

const SOFT_SHADOW =
  "0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -3px rgba(0,0,0,0.05)";

const FONT_STACK =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif";

const animationsCSS = `
@keyframes metricsSpin { to { transform: rotate(360deg); } }
@keyframes metricsPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
@keyframes metricsBarGrow { from { width: 0%; } }
@keyframes metricsShimmer {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
.metrics-pulse { animation: metricsPulse 2s ease-in-out infinite; }
.metrics-kpi { transition: transform .2s ease; }
.metrics-kpi:hover { transform: translateY(-4px); }
.metrics-bar-fill { animation: metricsBarGrow 1s ease-out; }
.metrics-row .metrics-action { opacity: 0; transition: opacity .15s; }
.metrics-row:hover .metrics-action { opacity: 1; }
.metrics-row:hover { background: ${PALETTE.surfaceContainerLow}; }
.metrics-ghost-btn:hover { background: ${PALETTE.primarySoft}; }
.metrics-link:hover { text-decoration: underline; }
.metrics-skeleton {
  background: linear-gradient(90deg, ${PALETTE.surfaceContainer} 0%, ${PALETTE.surfaceContainerLow} 50%, ${PALETTE.surfaceContainer} 100%);
  background-size: 800px 100%;
  animation: metricsShimmer 1.4s ease-in-out infinite;
  border-radius: 8px;
  display: inline-block;
}
`;

const styles = {
  page: {
    background: PALETTE.surfaceAlt,
    minHeight: "100vh",
    fontFamily: FONT_STACK,
    color: PALETTE.text,
    fontFeatureSettings: '"tnum" on, "lnum" on',
  },
  topbar: {
    position: "static",
    background: PALETTE.surfaceBright,
    borderBottom: `1px solid ${PALETTE.outlineVariant}`,
    boxShadow: "0 1px 2px rgba(0,0,0,.03)",
    padding: "24px 24px 20px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    flexWrap: "wrap",
    gap: "18px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    margin: 0,
    letterSpacing: "-0.015em",
    color: PALETTE.text,
    fontFamily: "'Montserrat', 'Inter', sans-serif",
  },
  subtitle: {
    color: PALETTE.textMuted,
    fontSize: "13px",
    marginTop: "4px",
  },
  segmented: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: `${PALETTE.surfaceContainerHigh}99`,
    borderRadius: "14px",
    padding: "4px",
    boxShadow: "inset 0 1px 2px rgba(15,28,44,0.06)",
  },
  segBtn: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "9px 20px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: active ? 700 : 600,
    fontSize: "13px",
    border: "none",
    background: active ? PALETTE.primary : "transparent",
    color: active ? "#FFFFFF" : PALETTE.textVariant,
    boxShadow: active ? SOFT_SHADOW : "none",
    transition: "all .2s ease",
  }),
  content: {
    maxWidth: "1600px",
    margin: "0 auto",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
  },
  kpiCard: {
    background: PALETTE.surface,
    borderRadius: "16px",
    padding: "16px",
    boxShadow: SOFT_SHADOW,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  kpiIconBox: (color, bg) => ({
    width: "44px",
    height: "44px",
    minWidth: "44px",
    borderRadius: "12px",
    background: bg,
    color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  kpiLabel: {
    fontSize: "11px",
    color: PALETTE.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 700,
    margin: 0,
    marginBottom: "2px",
  },
  kpiValue: (color) => ({
    fontSize: "24px",
    fontWeight: 800,
    lineHeight: "30px",
    letterSpacing: "-0.01em",
    color: color || PALETTE.text,
    fontVariantNumeric: "tabular-nums",
    margin: "2px 0 0",
  }),
  kpiHint: (color) => ({
    fontSize: "11px",
    color: color || PALETTE.textMuted,
    marginTop: "4px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  }),
  pulseDot: {
    width: "8px",
    height: "8px",
    borderRadius: "9999px",
    background: PALETTE.primary,
    display: "inline-block",
  },
  chartGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
    gap: "16px",
  },
  card: {
    background: PALETTE.surface,
    borderRadius: "16px",
    padding: "24px",
    boxShadow: SOFT_SHADOW,
  },
  cardWithoutPadding: {
    background: PALETTE.surface,
    borderRadius: "16px",
    boxShadow: SOFT_SHADOW,
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    gap: "16px",
    flexWrap: "wrap",
  },
  dateFilter: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: PALETTE.textMuted,
  },
  dateInput: {
    padding: "6px 10px",
    border: `1px solid ${PALETTE.outlineVariant}`,
    borderRadius: "6px",
    fontSize: "12px",
    fontFamily: "inherit",
    color: PALETTE.text,
    background: PALETTE.surface,
    outline: "none",
  },
  dateLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: PALETTE.textVariant,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  filtersPanel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    background: PALETTE.surface,
    borderRadius: "16px",
    padding: "12px 20px",
    boxShadow: SOFT_SHADOW,
    minWidth: "320px",
    maxWidth: "100%",
  },
  filterRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    textAlign: "center",
  },
  filterRowInline: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  filterLabel: {
    fontSize: "10px",
    fontWeight: 700,
    color: PALETTE.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
  },
  plataformaSelect: {
    padding: "10px 14px",
    border: "none",
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: "inherit",
    color: PALETTE.text,
    background: PALETTE.surfaceContainerLow,
    outline: "none",
    width: "100%",
    cursor: "pointer",
    fontWeight: 700,
  },
  filterDateInput: {
    flex: "1 1 130px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 14px",
    border: "none",
    borderRadius: "10px",
    fontSize: "13px",
    fontFamily: "inherit",
    color: PALETTE.text,
    background: PALETTE.surfaceContainerLow,
    outline: "none",
    fontVariantNumeric: "tabular-nums",
    fontWeight: 600,
    colorScheme: "light",
  },
  filterArrow: {
    color: PALETTE.textMuted,
    fontWeight: 700,
    fontSize: "13px",
    lineHeight: 1,
  },
  cardHeaderInline: {
    padding: "16px 24px",
    borderBottom: `1px solid ${PALETTE.outlineVariant}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardTitle: {
    fontSize: "20px",
    fontWeight: 600,
    margin: 0,
    color: PALETTE.text,
  },
  iconBtn: {
    color: PALETTE.outlineVariant,
    cursor: "pointer",
    background: "transparent",
    border: "none",
    padding: "4px",
    display: "flex",
    alignItems: "center",
  },
  barRow: { display: "flex", flexDirection: "column", gap: "8px" },
  barItem: { display: "flex", flexDirection: "column", gap: "4px" },
  barStack: { display: "flex", width: "100%", height: "100%" },
  barCaption: {
    display: "flex",
    gap: "16px",
    fontSize: "12px",
    color: PALETTE.textMuted,
    alignItems: "center",
  },
  barLabelRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    color: PALETTE.textVariant,
  },
  barLabel: { fontWeight: 500 },
  barValue: { fontWeight: 700, color: PALETTE.text },
  barTrack: {
    position: "relative",
    width: "100%",
    background: PALETTE.surfaceContainer,
    height: "22px",
    borderRadius: "9999px",
    overflow: "hidden",
  },
  barFill: (pct) => ({
    background: PALETTE.primary,
    height: "100%",
    width: `${pct}%`,
    borderRadius: "9999px",
    transition: "width 1s ease",
  }),
  barPctLabel: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 800,
    color: "#FFFFFF",
    // Contorno oscuro para que el texto blanco se lea tanto sobre el relleno
    // azul como sobre el tramo gris del track cuando el porcentaje es bajo.
    textShadow:
      "0 0 2px rgba(0,0,0,0.95), 0 0 3px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.8)",
    WebkitTextStroke: "0.4px rgba(0,0,0,0.55)",
    fontVariantNumeric: "tabular-nums",
    pointerEvents: "none",
    letterSpacing: "0.02em",
  },
  ghostBtn: {
    marginTop: "24px",
    width: "100%",
    padding: "10px 16px",
    color: PALETTE.primary,
    background: "transparent",
    fontWeight: 600,
    fontSize: "13px",
    border: `1px solid ${PALETTE.primaryBorder}`,
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background .15s",
  },
  donutWrap: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 0",
    minHeight: "280px",
  },
  donutSvg: { position: "relative", width: "192px", height: "192px" },
  donutCenter: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  donutValue: {
    fontSize: "32px",
    fontWeight: 900,
    color: PALETTE.text,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
  donutLabel: {
    fontSize: "11px",
    color: PALETTE.textVariant,
    textTransform: "uppercase",
    letterSpacing: "-0.02em",
    fontWeight: 500,
    marginTop: "6px",
  },
  legend: {
    marginTop: "24px",
    display: "flex",
    gap: "24px",
    fontSize: "14px",
    color: PALETTE.text,
  },
  legendItem: { display: "flex", alignItems: "center", gap: "8px" },
  legendDot: (color) => ({
    width: "12px",
    height: "12px",
    borderRadius: "9999px",
    background: color,
  }),
  table: { width: "100%", borderCollapse: "collapse", fontSize: "14px" },
  th: {
    textAlign: "left",
    padding: "12px 24px",
    background: PALETTE.surfaceContainerLow,
    color: PALETTE.textVariant,
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  td: {
    padding: "16px 24px",
    borderBottom: `1px solid ${PALETTE.outlineVariant}55`,
    color: PALETTE.text,
    fontVariantNumeric: "tabular-nums",
  },
  truckCell: { display: "flex", alignItems: "center", gap: "16px" },
  truckIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    background: PALETTE.surfaceContainerHigh,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: PALETTE.primary,
    flexShrink: 0,
  },
  badge: (variant) => {
    const v =
      variant === "active"
        ? { bg: PALETTE.emeraldBg, fg: PALETTE.emeraldText }
        : { bg: PALETTE.surfaceHighest, fg: PALETTE.textMuted };
    return {
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: "9999px",
      background: v.bg,
      color: v.fg,
      fontSize: "10px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    };
  },
  actionBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "6px",
    borderRadius: "9999px",
    color: PALETTE.primary,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  link: {
    color: PALETTE.primary,
    fontWeight: 600,
    cursor: "pointer",
    fontSize: "14px",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },
  empty: { textAlign: "center", padding: "40px", color: PALETTE.textMuted },
  spinner: {
    display: "inline-block",
    width: "32px",
    height: "32px",
    border: `3px solid ${PALETTE.border}`,
    borderTopColor: PALETTE.primary,
    borderRadius: "50%",
    animation: "metricsSpin 1s linear infinite",
  },
};

const getCachedIds = () => {
  let idPlataforma = null;
  let idCliente = null;
  try {
    const plataforma = JSON.parse(sessionStorage.getItem("plataforma"));
    if (plataforma && plataforma.id) idPlataforma = plataforma.id;
  } catch (e) { /* ignore */ }
  try {
    const usuario = JSON.parse(sessionStorage.getItem("usuario"));
    if (usuario) {
      idCliente =
        usuario.idCliente ||
        (usuario.cliente && usuario.cliente.id) ||
        usuario.clienteId ||
        null;
    }
  } catch (e) { /* ignore */ }
  if (!idCliente) {
    idCliente = sessionStorage.getItem("idCliente") || null;
  }
  return { idPlataforma, idCliente };
};

// Lee las plataformas asignadas al usuario desde sessionStorage.currentUser.
// Se deduplica por id porque un mismo usuario puede estar asignado a la misma
// plataforma con varios perfiles distintos.
const getPlataformasAsignadas = () => {
  try {
    const currentUser = JSON.parse(sessionStorage.getItem("currentUser"));
    if (!Array.isArray(currentUser)) return [];
    const map = new Map();
    currentUser.forEach((item) => {
      const p = item?.plataforma;
      if (p && p.id != null && !map.has(p.id)) {
        map.set(p.id, { id: p.id, nombre: p.nombre || `Plataforma ${p.id}` });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.nombre).localeCompare(String(b.nombre))
    );
  } catch (e) {
    return [];
  }
};

const pickNumber = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === "number") return v;
    if (typeof v === "string" && v !== "" && !isNaN(Number(v))) return Number(v);
  }
  return 0;
};

export const normalizeLinea = (t) => {
  const vehiculosAsignados = pickNumber(t, [
    "vehiculosAsignados", "vehiculos_asignados",
    "totalVehiculos", "total_vehiculos",
    "asignaciones", "unidadesAsignadas", "unidades_asignadas",
    "tractosAsignados", "remolquesAsignados",
    "asignados",
  ]);
  const serviciosAsignados = pickNumber(t, [
    "serviciosAsignados", "servicios_asignados",
    "totalServicios", "total_servicios",
    "servicios", "movimientos",
  ]);
  const activo = (t?.activo ?? t?.estatus ?? t?.estado);
  const activoFlag = (typeof activo === "boolean") ? activo
    : (typeof activo === "number") ? activo === 1
    : (typeof activo === "string") ? !["0", "false", "inactivo", "INACTIVO"].includes(activo)
    : true;

  return {
   
    nombre: t?.nombre ?? t?.razonSocial ?? t?.nombreCorto ?? `Transportista ${t?.id ?? ""}`,
    totalAsignacion: t?.totalAsignacion,
  };
};

const DONUT_ARC =
  "M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831";

// Donut apilable: recibe segmentos [{ value (porcentaje 0-100), color }].
const DonutSVG = ({ segments = [] }) => {
  let offset = 0;
  return (
    <svg
      width="192"
      height="192"
      viewBox="0 0 36 36"
      style={{ transform: "rotate(-90deg)" }}
    >
      <path
        d={DONUT_ARC}
        fill="none"
        stroke={PALETTE.surfaceContainer}
        strokeWidth="3"
        pathLength="100"
      />
      {segments.map((seg, i) => {
        const value = Math.max(0, Math.min(100, Number(seg.value) || 0));
        const node = (
          <path
            key={i}
            d={DONUT_ARC}
            fill="none"
            stroke={seg.color}
            strokeWidth="3"
            strokeDasharray={`${value} ${100 - value}`}
            strokeDashoffset={-offset}
            pathLength="100"
          />
        );
        offset += value;
        return node;
      })}
    </svg>
  );
};

const Skeleton = ({ width = "100%", height = 14, radius = 8, style }) => (
  <span
    className="metrics-skeleton"
    style={{
      width,
      height,
      borderRadius: radius,
      ...style,
    }}
  />
);

const SkeletonKpis = () => (
  <div style={styles.kpiGrid}>
    {[0, 1, 2, 3].map((i) => (
      <div key={i} style={styles.kpiCard}>
        <div style={{ flex: 1 }}>
          <Skeleton width={100} height={9} style={{ marginBottom: 10 }} />
          <Skeleton width={70} height={24} style={{ marginBottom: 8 }} />
          <Skeleton width={120} height={9} />
        </div>
        <Skeleton width={44} height={44} radius={12} />
      </div>
    ))}
  </div>
);

const SkeletonBars = ({ rows = 8 }) => (
  <div style={styles.barRow}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={styles.barItem}>
        <div style={styles.barLabelRow}>
          <Skeleton width={`${40 + ((i * 7) % 30)}%`} height={12} />
          <Skeleton width={50} height={12} />
        </div>
        <Skeleton width="100%" height={22} radius={9999} />
      </div>
    ))}
  </div>
);

const SkeletonDonut = () => (
  <div style={styles.donutWrap}>
    <Skeleton width={192} height={192} radius={9999} />
    <div style={{ ...styles.legend, gap: 24 }}>
      <Skeleton width={120} height={12} />
      <Skeleton width={120} height={12} />
    </div>
  </div>
);

// Fechas en hora local (no UTC). Usar toISOString() genera off-by-one cuando el
// reloj local del usuario está rezagado vs UTC (ej. en México pasadas las 18hrs).
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const firstDayOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

// Tamaño de página usado solo al exportar: grande para recorrer todas las
// páginas con la menor cantidad posible de llamadas al endpoint.
const EXPORT_PAGE_SIZE = 100;

// Exporta un renglón por SERVICIO (no por línea): cada línea trae su propio
// arreglo `movimientos` (igual al que alimenta el expansor de la tabla), así
// que el CSV queda con el mismo detalle que se ve desplegado en el front.
const downloadCSVRows = (rows) => {
  const header = [
    "Línea de transporte",
    "Total asignación línea (%)",
    "PO",
    "Booking",
    "Contenedor",
    "Cliente",
    "Vertical",
    "Ruta Estatus",
    "Tipo de Movimiento",
    "Operador",
    "Contacto Operador",
    "Unidad",
    "Placa Tractor",
    "Placa Remolque",
    "Cita",
  ];
  const csvRow = (values) =>
    values.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",");

  const lines = [header.join(",")];

  rows.forEach((r) => {
    const movimientos = Array.isArray(r?.movimientos) ? r.movimientos : [];
    if (movimientos.length === 0) {
      lines.push(
        csvRow([r?.nombre, r?.totalAsignacion, "", "", "", "", "", "", "", "", "", "", "", "", ""])
      );
      return;
    }
    movimientos.forEach((mv) => {
      const patio = mv?.movimientoPatio || {};
      lines.push(
        csvRow([
          r?.nombre,
          r?.totalAsignacion,
          mv?.po,
          mv?.booking,
          mv?.contenedor,
          mv?.cliente?.nombre,
          mv?.cliente?.vertical,
          mv?.rutaEstatus,
          mv?.tipoMovimiento,
          patio.operadorNombre,
          patio.operadorContacto,
          patio.idRemolque?.nombre,
          patio.tractorPlaca,
          patio.remolquePlaca,
          mv?.citaProgramada,
        ])
      );
    });
  });
  // BOM para que Excel reconozca UTF-8 (acentos correctos).
  const blob = new Blob(["\uFEFF" + lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `servicios-transportistas-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const MetricasTransportistaDashboard = () => {
  const { idPlataforma: idPlataformaSesion, idCliente } = useMemo(getCachedIds, []);
  const plataformasAsignadas = useMemo(getPlataformasAsignadas, []);
  // La plataforma seleccionada arranca con la que el usuario tiene activa en
  // sesión; si no está dentro de las asignadas, cae a la primera disponible.
  const [idPlataforma, setIdPlataforma] = useState(() => {
    if (
      idPlataformaSesion &&
      plataformasAsignadas.some((p) => p.id === idPlataformaSesion)
    ) {
      return idPlataformaSesion;
    }
    return plataformasAsignadas[0]?.id ?? idPlataformaSesion ?? null;
  });
  const [view, setView] = useState("general");
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coberturaLoading, setCoberturaLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fechaDesde, setFechaDesde] = useState(firstDayOfMonthISO());
  const [fechaHasta, setFechaHasta] = useState(todayISO());
  const [cobertura, setCobertura] = useState(null);
  const [topLineas, setTopLineas] = useState(null);
  const [topLoading, setTopLoading] = useState(false);

  // Tabla "Líneas de transporte": paginación, búsqueda y orden del lado del
  // servidor contra transportistas/plataforma/{id} con query params.
  // Devuelve un Page<TopTransport> => content: [{ nombre, totalAsignacion }].
  const [tablaRows, setTablaRows] = useState([]);
  const [tablaLoading, setTablaLoading] = useState(false);
  const [tablaError, setTablaError] = useState(null);
  const [tablaPage, setTablaPage] = useState(0); // 0-based, como Spring
  const [tablaSize, setTablaSize] = useState(10);
  const [tablaSearch, setTablaSearch] = useState("");
  // Evita condición de carrera: si el usuario escribe rápido, varias peticiones
  // quedan en vuelo y pueden resolver desordenadas. Solo se aplica la respuesta
  // de la última petición disparada (ver useEffect de tablaRows más abajo).
  const tablaRequestIdRef = useRef(0);
  // sortBy/sortDirection arrancan con el default del backend (idMovimiento/asc)
  // para garantizar que la primera carga use un campo de orden válido. Los
  // encabezados de la tabla permiten ordenar por "nombre"/"totalAsignacion".
  const [tablaSortBy, setTablaSortBy] = useState("idMovimiento");
  const [tablaSortDirection, setTablaSortDirection] = useState("asc");
  const [tablaTotalElements, setTablaTotalElements] = useState(0);
  const [tablaTotalPages, setTablaTotalPages] = useState(0);

  // Preview "Resumen por línea de transporte" (tab Análisis general): primeras
  // filas del mismo endpoint paginado, para que coincida con la tabla completa.
  const [resumenRows, setResumenRows] = useState([]);
  const [resumenLoading, setResumenLoading] = useState(false);

  // Exportación CSV: recorre todas las páginas del endpoint antes de generar
  // el archivo, mostrando el loader del camión mientras tanto.
  const [exportandoCSV, setExportandoCSV] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);


  useEffect(() => {
    if (!idPlataforma) {
      setError("No se encontró la plataforma en sesión. Vuelve a iniciar sesión.");
      setLoading(false);
      return;
    }
    setLoading(true);
    Crud_Catalogos(`transportistas/plataforma/${idPlataforma}`, "", "get", "")
      .then((res) => {
        const data = Array.isArray(res) ? res : (res?.content || []);
        setLineas(data.map(normalizeLinea));
        setError(null);
      })
      .catch((err) => {
        console.error("Error al cargar transportistas:", err);
        setError("No se pudo cargar la información de transportistas.");
        setLineas([]);
      })
      .finally(() => setLoading(false));
  }, [idPlataforma]);

  // Cobertura de asignación filtrada por fechas.
  // El backend devuelve { asignados, total }; "no asignados" = total - asignados.
  useEffect(() => {
    if (!idPlataforma || !fechaDesde || !fechaHasta) return;
    if (fechaDesde > fechaHasta) return;
    setCoberturaLoading(true);
    Crud_Catalogos(
      `movimientosPatio/plataforma/${idPlataforma}/fecha1/${fechaDesde}/fecha2/${fechaHasta}`,
      "",
      "get",
      ""
    )
      .then((res) => {
        const d = res || {};
        const asignadosTransporte = Number(d.asignadosTransporte ?? 0);
        const asignadosPatios = Number(d.asignadosPatios ?? 0);
        const total = Number(d.total ?? 0);
        setCobertura({
          conAsignacionTransporte: asignadosTransporte,
          conAsignacionPatios: asignadosPatios,
          sinAsignacion: Math.max(total - (asignadosTransporte + asignadosPatios), 0),
          total:total
        });
      })
      .catch((err) => {
        console.warn(
          "Endpoint de cobertura no disponible, fallback a cálculo local:",
          err?.response?.status
        );
        setCobertura(null); // fallback: cálculo local en metrics
      })
      .finally(() => setCoberturaLoading(false));
  }, [idPlataforma, fechaDesde, fechaHasta]);

  // Top 10 líneas de transporte por periodo.
  useEffect(() => {
    if (!idPlataforma || !fechaDesde || !fechaHasta) return;
    if (fechaDesde > fechaHasta) return;
    setTopLoading(true);
    Crud_Catalogos(
      `movimientosPatio/top10/plataforma/${idPlataforma}/fecha1/${fechaDesde}/fecha2/${fechaHasta}`,
      "",
      "get",
      ""
    )
      .then((res) => {
        const data = Array.isArray(res) ? res : (res?.content || []);
        setTopLineas(
          data.map((t) => ({
            nombre: t?.nombre ?? "",
            totalAsignacion: Number(t?.totalAsignacion ?? 0),
            asignadosTransporte: Number(t?.asignadosTransporte ?? 0),
            asignadosPatios: Number(t?.asignadosPatios ?? 0),
            totalServicios: Number(t?.totalServicios ?? 0),
          }))
        );
      })
      .catch((err) => {
        console.warn(
          "Endpoint top10 no disponible, fallback a cálculo local:",
          err?.response?.status
        );
        setTopLineas(null);
      })
      .finally(() => setTopLoading(false));
  }, [idPlataforma, fechaDesde, fechaHasta]);

  // Tabla "Líneas de transporte" con paginación/búsqueda/orden del servidor.
  // Solo se consulta cuando el tab está activo para no disparar llamadas de más.
  useEffect(() => {
    if (view !== "lineas") return;
    if (!idPlataforma || !fechaDesde || !fechaHasta) return;
    if (fechaDesde > fechaHasta) return;
    // Se marca esta petición como la más reciente; si otra más nueva se
    // dispara antes de que esta responda, su resultado se descarta abajo.
    const requestId = ++tablaRequestIdRef.current;
    setTablaLoading(true);
    setTablaError(null);
    const qs = new URLSearchParams({
      fechaInicio: fechaDesde,
      fechaFin: fechaHasta,
      transporte: tablaSearch,
      page: tablaPage,
      size: tablaSize,
      sortBy: tablaSortBy,
      sortDirection: tablaSortDirection,
    }).toString();
    Crud_Catalogos(
      `movimientosPatio/plataforma/${idPlataforma}?${qs}`,
      "",
      "get",
      ""
    )
      .then((res) => {
        if (tablaRequestIdRef.current !== requestId) return; // respuesta obsoleta
        const content = Array.isArray(res) ? res : (res?.content || []);
        setTablaRows(content);
        setTablaTotalElements(Number(res?.totalElements ?? content.length));
        setTablaTotalPages(Number(res?.totalPages ?? 1));
      })
      .catch((err) => {
        if (tablaRequestIdRef.current !== requestId) return;
        console.error("Error al cargar líneas de transporte:", err);
        setTablaError("No se pudo cargar la tabla de líneas de transporte.");
        setTablaRows([]);
        setTablaTotalElements(0);
        setTablaTotalPages(0);
      })
      .finally(() => {
        if (tablaRequestIdRef.current !== requestId) return;
        setTablaLoading(false);
      });
  }, [
    view,
    idPlataforma,
    fechaDesde,
    fechaHasta,
    tablaSearch,
    tablaPage,
    tablaSize,
    tablaSortBy,
    tablaSortDirection,
  ]);

  // Resumen para el preview del tab "Análisis general": primeras 5 filas del
  // endpoint paginado (orden por defecto del backend, sin búsqueda).
  useEffect(() => {
    if (view !== "general") return;
    if (!idPlataforma || !fechaDesde || !fechaHasta) return;
    if (fechaDesde > fechaHasta) return;
    setResumenLoading(true);
    const qs = new URLSearchParams({
      fechaInicio: fechaDesde,
      fechaFin: fechaHasta,
      transporte: "",
      page: 0,
      size: 5,
      sortBy: "idMovimiento",
      sortDirection: "asc",
    }).toString();
    Crud_Catalogos(
      `movimientosPatio/plataforma/${idPlataforma}?${qs}`,
      "",
      "get",
      ""
    )
      .then((res) => {
        const content = Array.isArray(res) ? res : (res?.content || []);
        setResumenRows(content.slice(0, 5));
      })
      .catch((err) => {
        console.error("Error al cargar resumen por línea:", err);
        setResumenRows([]);
      })
      .finally(() => setResumenLoading(false));
  }, [view, idPlataforma, fechaDesde, fechaHasta]);

  // Exporta TODAS las líneas del periodo: recorre página por página el endpoint
  // (respetando búsqueda/orden actuales) y arma el CSV con el total acumulado.
  const handleExportCSV = async () => {
    if (exportandoCSV) return;
    if (!idPlataforma || !fechaDesde || !fechaHasta || fechaDesde > fechaHasta) {
      return;
    }
    setExportandoCSV(true);
    setExportProgress(0);
    try {
      const acumulado = [];
      let page = 0;
      let totalPages = 1;
      do {
        const qs = new URLSearchParams({
          fechaInicio: fechaDesde,
          fechaFin: fechaHasta,
          transporte: tablaSearch,
          page,
          size: EXPORT_PAGE_SIZE,
          sortBy: tablaSortBy,
          sortDirection: tablaSortDirection,
        }).toString();
        const res = await Crud_Catalogos(
          `movimientosPatio/plataforma/${idPlataforma}?${qs}`,
          "",
          "get",
          ""
        );
        const content = Array.isArray(res) ? res : (res?.content || []);
        acumulado.push(...content);
        totalPages = Number(res?.totalPages ?? 1);
        page += 1;
        // Progreso = páginas leídas / total (se reserva el 100% para el final).
        setExportProgress(
          Math.min(99, Math.round((page / Math.max(totalPages, 1)) * 100))
        );
        if (content.length === 0) break; // salvaguarda si no hay totalPages
      } while (page < totalPages);

      setExportProgress(100);
      downloadCSVRows(acumulado);
    } catch (err) {
      console.error("Error al exportar CSV:", err);
    } finally {
      setExportandoCSV(false);
    }
  };

  const metrics = useMemo(() => {
    const total = lineas.length;
    const activos = lineas.filter((l) => l.activo).length;
 
    return { total, activos};
  }, [lineas]);

  const fmt = (n) => (n || 0).toLocaleString("es-MX");

  const renderTopbar = () => (
    <header style={styles.topbar}>
      <div>
        <h1 style={styles.title}>Métricas de Transporte</h1>
        <div style={styles.subtitle}>
          Uso de la plataforma por línea de transporte

          {idCliente ? ` · Cliente #${idCliente}` : ""}
        </div>
      </div>
      <div style={styles.filtersPanel}>
        {plataformasAsignadas.length > 1 && (
          <div style={styles.filterRow}>
            <label style={styles.filterLabel} htmlFor="filtro-plataforma">
              Plataforma
            </label>
            <select
              id="filtro-plataforma"
              style={styles.plataformaSelect}
              value={idPlataforma ?? ""}
              onChange={(e) => {
                const nuevoId = Number(e.target.value);
                setIdPlataforma(Number.isNaN(nuevoId) ? null : nuevoId);
                setTablaPage(0);
              }}
            >
              {plataformasAsignadas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={styles.filterRow}>
          <label style={styles.filterLabel}>Período</label>
          <div style={styles.filterRowInline}>
            <label style={styles.filterDateInput}>
              <CalendarDays size={14} color={PALETTE.primary} />
              <input
                type="date"
                aria-label="Fecha desde"
                value={fechaDesde}
                max={fechaHasta}
                onChange={(e) => {
                  setTablaPage(0);
                  setFechaDesde(e.target.value);
                }}
                style={{ border: "none", background: "transparent", outline: "none", font: "inherit", color: "inherit", padding: 0 }}
              />
            </label>
            <span style={styles.filterArrow} aria-hidden="true">→</span>
            <label style={styles.filterDateInput}>
              <CalendarDays size={14} color={PALETTE.primary} />
              <input
                type="date"
                aria-label="Fecha hasta"
                value={fechaHasta}
                min={fechaDesde}
                max={todayISO()}
                onChange={(e) => {
                  setTablaPage(0);
                  setFechaHasta(e.target.value);
                }}
                style={{ border: "none", background: "transparent", outline: "none", font: "inherit", color: "inherit", padding: 0 }}
              />
            </label>
          </div>
        </div>
      </div>
      <div style={styles.segmented}>
        <button style={styles.segBtn(view === "general")} onClick={() => setView("general")}>
          <Network size={15} />
          Análisis general
        </button>
        <button style={styles.segBtn(view === "lineas")} onClick={() => setView("lineas")}>
          <Truck size={15} />
          Líneas de transporte
        </button>
      </div>
    </header>
  );

  const renderKpis = () => {
    const asignTransporte = cobertura ? cobertura.conAsignacionTransporte : 0;
    const asignPatios = cobertura ? cobertura.conAsignacionPatios : 0;
    const servicios = cobertura ? cobertura.total : 0;
    const pctAsignado = servicios > 0 ? ((asignTransporte + asignPatios) / servicios) * 100 : 0;
    const pctTransporte = servicios > 0 ? (asignTransporte / servicios) * 100 : 0;
    return (
      <div style={styles.kpiGrid}>
        <div className="metrics-kpi" style={styles.kpiCard}>
          <div>
            <p style={styles.kpiLabel}>Líneas Activas</p>
            <h3 style={styles.kpiValue()}>{fmt(lineas.length)}</h3>
            <p style={styles.kpiHint()}>líneas registradas en la plataforma</p>
          </div>
          <div style={styles.kpiIconBox(PALETTE.primary, PALETTE.surfaceContainerLow)}>
            <Network size={20} />
          </div>
        </div>
        <div className="metrics-kpi" style={styles.kpiCard}>
          <div>
            <p style={styles.kpiLabel}>Total Servicios</p>
            <h3 style={styles.kpiValue()}>{fmt(servicios)}</h3>
            <p style={styles.kpiHint(PALETTE.primary)}>
              <CheckCircle2 size={12} /> {pctAsignado.toFixed(1)}% con asignación
            </p>
          </div>
          <div style={styles.kpiIconBox(PALETTE.primary, PALETTE.surfaceContainerLow)}>
            <Truck size={20} />
          </div>
        </div>
        <div className="metrics-kpi" style={styles.kpiCard}>
          <div>
            <p style={styles.kpiLabel}>Asignación Directa</p>
            <h3 style={styles.kpiValue(PALETTE.emeraldText)}>{pctTransporte.toFixed(1)}%</h3>
            <p style={styles.kpiHint(PALETTE.emeraldText)}>
              <CheckCircle2 size={12} /> {fmt(asignTransporte)} vehículos
            </p>
          </div>
          <div style={styles.kpiIconBox(PALETTE.emeraldText, PALETTE.emeraldBg)}>
            <ShieldCheck size={20} />
          </div>
        </div>
        <div className="metrics-kpi" style={styles.kpiCard}>
          <div>
            <p style={styles.kpiLabel}>Asignados por Patio</p>
            <h3 style={styles.kpiValue(PALETTE.amber)}>{fmt(asignPatios)}</h3>
            <p style={styles.kpiHint()}>Vía correo, WhatsApp o llamada</p>
          </div>
          <div style={styles.kpiIconBox(PALETTE.amber, PALETTE.amberBg)}>
            <Warehouse size={20} />
          </div>
        </div>
      </div>
    );
  };

  const renderCharts = () => {
    const hasTop = Array.isArray(topLineas) && topLineas.length > 0;
    const conAsigTransporte = cobertura ? cobertura.conAsignacionTransporte : 0;
    const conAsigPatios = cobertura ? cobertura.conAsignacionPatios : 0;
    const sinAsig = cobertura ? cobertura.sinAsignacion : 0;
    const donutTotal = cobertura ? cobertura.total : 0;
    const pctTransporte = donutTotal > 0 ? (conAsigTransporte / donutTotal) * 100 : 0;
    const pctPatios = donutTotal > 0 ? (conAsigPatios / donutTotal) * 100 : 0;
    const pctSin = donutTotal > 0 ? (sinAsig / donutTotal) * 100 : 0;
    const pctAsignado =
      donutTotal > 0
        ? ((conAsigTransporte + conAsigPatios) / donutTotal) * 100
        : 0;
    const datesInvalid = fechaDesde > fechaHasta;
    // Solo se pinta el top10 si está cargando o si el API devolvió datos.
    const showTopCard = topLoading || hasTop;
    return (
      <div style={styles.chartGrid}>
        {showTopCard && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h4 style={styles.cardTitle}>Top 10 líneas por vehículos asignados</h4>
              <button style={styles.iconBtn} aria-label="opciones">
                <MoreHorizontal size={20} />
              </button>
            </div>
            {topLoading ? (
              <SkeletonBars rows={8} />
            ) : (
              <div
                style={styles.barRow}
                key={`top-${fechaDesde}-${fechaHasta}-${topLineas.length}`}
              >
                {topLineas.map((l, idx) => {
                  const totalServ = Number(l.totalServicios ?? 0);
                  const t = Number(l.asignadosTransporte ?? 0);
                  const p = Number(l.asignadosPatios ?? 0);
                  const pctT = totalServ > 0 ? (t / totalServ) * 100 : 0;
                  const pctP = totalServ > 0 ? (p / totalServ) * 100 : 0;
                  return (
                    <div key={`${l.nombre}-${idx}`} style={styles.barItem}>
                      <div style={styles.barLabelRow}>
                        <span style={styles.barLabel}>{l.nombre}</span>
                        <span style={styles.barValue}>{fmt(l.totalAsignacion)}%</span>
                      </div>
                      <div
                        style={styles.barTrack}
                        title={`Transporte: ${t} · Patios: ${p} · Servicios: ${totalServ}`}
                      >
                        <div style={styles.barStack}>
                          <div
                            className="metrics-bar-fill"
                            style={{ width: `${pctT}%`, height: "100%", background: PALETTE.primary }}
                          />
                          <div
                            className="metrics-bar-fill"
                            style={{ width: `${pctP}%`, height: "100%", background: PALETTE.amber }}
                          />
                        </div>
                      </div>
                      <div style={styles.barCaption}>
                        <span style={styles.legendItem}>
                          <span style={styles.legendDot(PALETTE.primary)} /> Transporte {fmt(t)}
                        </span>
                        <span style={styles.legendItem}>
                          <span style={styles.legendDot(PALETTE.amber)} /> Patios {fmt(p)}
                        </span>
                        <span style={styles.legendItem}>
                          Servicios {fmt(totalServ)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {!topLoading && hasTop && (
              <button
                className="metrics-ghost-btn"
                style={styles.ghostBtn}
                onClick={() => setView("lineas")}
              >
                Ver todos los datos
              </button>
            )}
          </div>
        )}

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h4 style={styles.cardTitle}>Cobertura de asignación</h4>
          </div>
          {datesInvalid ? (
            <div style={styles.empty}>
              El rango de fechas no es válido.
            </div>
          ) : coberturaLoading ? (
            <SkeletonDonut />
          ) : (
            <div style={styles.donutWrap}>
              <div style={styles.donutSvg}>
                <DonutSVG
                  segments={[
                    { value: pctTransporte, color: PALETTE.primary },
                    { value: pctPatios, color: PALETTE.amber },
                    { value: pctSin, color: PALETTE.outlineVariant },
                  ]}
                />
                <div style={styles.donutCenter}>
                  <span style={styles.donutValue}>{pctAsignado.toFixed(1)}%</span>
                  <span style={styles.donutLabel}>Asignación</span>
                </div>
              </div>
              <div style={{ ...styles.legend, flexWrap: "wrap", justifyContent: "center" }}>
                <div style={styles.legendItem}>
                  <span style={styles.legendDot(PALETTE.primary)} />
                  <span>
                    Transporte · <strong>{fmt(conAsigTransporte)}</strong> (
                    {pctTransporte.toFixed(1)}%)
                  </span>
                </div>
                <div style={styles.legendItem}>
                  <span style={styles.legendDot(PALETTE.amber)} />
                  <span>
                    Patios · <strong>{fmt(conAsigPatios)}</strong> (
                    {pctPatios.toFixed(1)}%)
                  </span>
                </div>
                <div style={styles.legendItem}>
                  <span style={styles.legendDot(PALETTE.outlineVariant)} />
                  <span>
                    Sin asignación · <strong>{fmt(sinAsig)}</strong> (
                    {pctSin.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderListaPreview = () => {
    const clampPct = (n) => Math.max(0, Math.min(100, Number(n || 0)));
    return (
      <div style={styles.cardWithoutPadding}>
        <div style={styles.cardHeaderInline}>
          <h4 style={styles.cardTitle}>Resumen por línea de transporte</h4>
          <a
            className="metrics-link"
            style={styles.link}
            onClick={() => setView("lineas")}
          >
            Ver todas <ArrowRight size={16} />
          </a>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Línea de transporte</th>
                <th style={styles.th}>Total asignación</th>
              </tr>
            </thead>
            <tbody>
              {resumenLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td style={styles.td}>
                      <div style={styles.truckCell}>
                        <Skeleton width={32} height={32} radius={6} />
                        <Skeleton width={160} height={12} />
                      </div>
                    </td>
                    <td style={styles.td}>
                      <Skeleton width="100%" height={22} radius={9999} />
                    </td>
                  </tr>
                ))
              ) : (
                resumenRows.map((l, idx) => (
                  <tr key={`${l.nombre ?? "linea"}-${idx}`} className="metrics-row">
                    <td style={styles.td}>
                      <div style={styles.truckCell}>
                        <span style={styles.truckIcon}>
                          <Truck size={16} />
                        </span>
                        <span style={{ fontWeight: 500 }}>{l.nombre || "—"}</span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.barTrack}>
                        <div
                          className="metrics-bar-fill"
                          style={styles.barFill((l.totalAsignacion))}
                        />
                        <span style={styles.barPctLabel}>
                          {fmt(l.totalAsignacion)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!resumenLoading && resumenRows.length === 0 && (
                <tr>
                  <td colSpan={2} style={{ ...styles.td, ...styles.empty }}>
                    Sin líneas registradas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSkeletonBody = () => (
    <>
      <SkeletonKpis />
      <div style={styles.chartGrid}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <Skeleton width={260} height={18} />
            <Skeleton width={20} height={20} radius={4} />
          </div>
          <SkeletonBars rows={8} />
        </div>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <Skeleton width={200} height={18} />
            <Skeleton width={220} height={28} />
          </div>
          <SkeletonDonut />
        </div>
      </div>
      <div style={styles.cardWithoutPadding}>
        <div style={styles.cardHeaderInline}>
          <Skeleton width={240} height={18} />
          <Skeleton width={80} height={14} />
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Línea de transporte</th>
                <th style={styles.th}>Total asignación</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td style={styles.td}>
                    <div style={styles.truckCell}>
                      <Skeleton width={32} height={32} radius={6} />
                      <Skeleton width={160} height={12} />
                    </div>
                  </td>
                  <td style={styles.td}>
                    <Skeleton width="100%" height={22} radius={9999} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const renderBody = () => {
    // El skeleton de página completa es para el tab "Análisis general".
    // En "Líneas de transporte" la propia tabla muestra su skeleton.
    if (loading && view === "general") {
      return renderSkeletonBody();
    }
    if (error && view === "general") {
      return (
        <div style={{ ...styles.card, borderColor: "#FCA5A5" }}>
          <div style={{ color: "#B91C1C", fontWeight: 600 }}>⚠ {error}</div>
        </div>
      );
    }
    if (view === "lineas") {
      return (
        <ListaTransportistas
          rows={tablaRows}
          loading={tablaLoading}
          error={tablaError}
          page={tablaPage}
          size={tablaSize}
          totalElements={tablaTotalElements}
          totalPages={tablaTotalPages}
          search={tablaSearch}
          sortBy={tablaSortBy}
          sortDirection={tablaSortDirection}
          fechaDesde={fechaDesde}
          fechaHasta={fechaHasta}
          exporting={exportandoCSV}
          exportProgress={exportProgress}
          onExport={handleExportCSV}
          onSearchChange={(v) => {
            setTablaPage(0);
            setTablaSearch(v);
          }}
          onPageChange={(p) => setTablaPage(p)}
          onSortChange={(field) => {
            setTablaPage(0);
            if (tablaSortBy === field) {
              setTablaSortDirection((d) => (d === "asc" ? "desc" : "asc"));
            } else {
              setTablaSortBy(field);
              setTablaSortDirection("asc");
            }
          }}
          onFechaDesdeChange={(v) => {
            setTablaPage(0);
            setFechaDesde(v);
          }}
          onFechaHastaChange={(v) => {
            setTablaPage(0);
            setFechaHasta(v);
          }}
        />
      );
    }
    return (
      <>
        {renderKpis()}
        {renderCharts()}
        {renderListaPreview()}
      </>
    );
  };

  return (
    <>
      <style>{animationsCSS}</style>
      <div style={styles.page}>
        {renderTopbar()}
        <div style={styles.content}>{renderBody()}</div>
      </div>
    </>
  );
};

export default MetricasTransportistaDashboard;
